import { useState } from "react";
import { Trash2, Check, Ban, ChevronDown, Pencil } from "lucide-react";
import { toast } from "react-hot-toast";
import { updateGalleryStatus } from "../../../services/galleryServices";
import DeleteConfirmationModal from "../members/DeleteMemberModal";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";

export const GalleryTable = ({
  galleries = [],
  refetch,
  startIndex = 0,
  permissionsData,
}) => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedGallery, setSelectedGallery] = useState(null);

  const navigate = useNavigate();

  const role = Cookies.get("role");

  let canEdit = false;
  let canDelete = false;

  if (role === "admin") {
    canEdit = true;
    canDelete = true;
  } else if (role === "maintainer") {
    const modulePermissions = permissionsData?.permissions?.find(
      (p) => p.module === "Gallery"
    );
    canEdit = !!modulePermissions?.edit;
    canDelete = !!modulePermissions?.delete;
  }

  const showActions = canEdit || canDelete;

  const handleGalleryAction = async (galleryId, action) => {
    try {
      const result = await updateGalleryStatus({ galleryId, action });
      toast.success(result.message || `Gallery ${action}d successfully`);
      if (refetch) refetch();
    } catch (error) {
      toast.error(error.message || `Failed to ${action} gallery`);
    }
  };

  const handleStatusChange = async (gallery, newStatus) => {
    const action = newStatus === "active" ? "activate" : "deactivate";
    try {
      const result = await updateGalleryStatus({
        galleryId: gallery._id,
        action,
      });
      toast.success(result.message || `Gallery ${action}d successfully`);
      refetch();
      setOpenDropdown(null);
    } catch (error) {
      toast.error(error.message || `Failed to ${action} gallery`);
    }
  };

  const openDeleteModal = (gallery) => {
    setSelectedGallery(gallery);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedGallery) return;
    await handleGalleryAction(selectedGallery._id, "delete");
    setDeleteModalOpen(false);
    setSelectedGallery(null);
  };

  const getStatusConfig = (status) => {
    if (status === "active") {
      return {
        icon: <Check size={14} />,
        bgColor: "bg-green",
        textColor: "text-white",
        hoverColor: "hover:bg-green-600",
        label: "Active",
      };
    } else {
      return {
        icon: <Ban size={14} />,
        bgColor: "bg-gray-500",
        textColor: "text-white",
        hoverColor: "hover:bg-gray-600",
        label: "Inactive",
      };
    }
  };

  const StatusDropdown = ({ gallery, currentStatus }) => {
    const isOpen = openDropdown === gallery._id;
    const statusConfig = getStatusConfig(currentStatus);

    const statusOptions = [
      {
        value: "active",
        label: "Active",
        icon: <Check size={14} />,
        color: "text-green",
      },
      {
        value: "in-active",
        label: "Inactive",
        icon: <Ban size={14} />,
        color: "text-gray-600",
      },
    ];

    return (
      <div className="relative">
        <button
          onClick={() => setOpenDropdown(isOpen ? null : gallery._id)}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${statusConfig.bgColor} ${statusConfig.textColor} ${statusConfig.hoverColor}`}
        >
          {statusConfig.icon}
          <span className="capitalize">{statusConfig.label}</span>
          <ChevronDown
            size={12}
            className={`transform transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </button>

        {isOpen && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setOpenDropdown(null)}
            />
            <div className="absolute top-full left-0 mt-1 w-32 bg-white border border-gray-200 rounded-md shadow-lg z-20">
              {statusOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleStatusChange(gallery, option.value)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                    currentStatus === option.value ? "bg-gray-100" : ""
                  }`}
                  disabled={currentStatus === option.value}
                >
                  <span className={option.color}>{option.icon}</span>
                  <span className="text-gray-700 capitalize">
                    {option.label}
                  </span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="bg-white rounded-xl h-full shadow-sm whitespace-nowrap !overflow-x-auto">
        <table className="min-w-full table-fixed text-sm border border-gray-300">
          <thead className="bg-[#4F22D8] text-white text-base">
            <tr>
              <th className="py-3 px-4 text-left font-bold border-r border-gray-400">
                No
              </th>
              <th className="py-3 px-4 text-left font-bold border-r border-gray-400">
                Title
              </th>
              <th className="py-3 px-4 text-left font-bold border-r border-gray-400">
                Number of images
              </th>
              <th className="py-3 px-4 text-left font-bold border-r border-gray-400">
                Status
              </th>
              {showActions && (
                <th className="py-3 px-4 text-left font-bold">Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {galleries.length === 0 ? (
              <tr>
                <td
                  colSpan={showActions ? 5 : 4}
                  className="text-center py-4 text-gray-500"
                >
                  No galleries found.
                </td>
              </tr>
            ) : (
              galleries.map((row, idx) => (
                <tr
                  key={row._id}
                  className="border-t border-gray-300 text-[15px] hover:bg-gray-50 transition-colors"
                >
                  <td className="px-4 py-3 font-normal text-black border-r border-gray-300">
                    {startIndex + idx + 1}
                  </td>
                  <td className="px-4 py-3 font-normal text-gray-900 border-r border-gray-300">
                    {row.name}
                  </td>
                  <td className="px-4 py-3 text-black border-r border-gray-300">
                    {row.images?.length || 0}
                  </td>
                  <td className="px-4 py-3 border-r border-gray-300">
                    {canEdit ? (
                      <StatusDropdown
                        gallery={row}
                        currentStatus={row.status || "in-active"}
                      />
                    ) : (
                      <span className="capitalize">
                        {row.status || "in-active"}
                      </span>
                    )}
                  </td>

                  {showActions && (
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {canEdit && (
                          <button
                            className="text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 transition-colors"
                            onClick={() =>
                              navigate(`/admin/gallery/edit-gallery/${row._id}`)
                            }
                          >
                            <Pencil size={14} /> Edit
                          </button>
                        )}

                        {canEdit && canDelete && (
                          <div className="w-px h-4 bg-gray-300" />
                        )}

                        {canDelete && (
                          <button
                            className="text-red hover:text-red font-medium flex items-center gap-1 transition-colors"
                            onClick={() => openDeleteModal(row)}
                          >
                            <Trash2 size={14} /> Delete
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {selectedGallery && (
        <DeleteConfirmationModal
          isOpen={deleteModalOpen}
          onRequestClose={() => setDeleteModalOpen(false)}
          onDelete={confirmDelete}
          itemName={selectedGallery.name}
          itemType="Gallery"
        />
      )}
    </>
  );
};
